import { useMemo } from 'react';
import { OverlayProvider } from '@saviynt/design-system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { IntlProvider } from 'react-intl';

import { routeTree } from '../../../routeTree.gen';
import enMessages from '../../locales/en.json';

import { AlertBannerProvider } from './AlertBannerContext';

type MessageEntry = string | { defaultMessage: string; description?: string };

const messages: Record<string, string> = Object.fromEntries(
  Object.entries(enMessages as Record<string, MessageEntry>).map(
    ([id, entry]) => [
      id,
      typeof entry === 'string' ? entry : (entry?.defaultMessage ?? id),
    ]
  )
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

export function MockRouterProvider() {
  const router = useMemo(() => {
    const pathname = window.location.pathname;
    const isRunningAsMfe =
      pathname.startsWith('/ECMv6/agent') || pathname.startsWith('/agent');
    // TanStack Router basepath should match the full prefix from the pathname
    const basepath = isRunningAsMfe
      ? pathname.startsWith('/ECMv6')
        ? '/ECMv6/agent'
        : '/agent'
      : '/';

    const routerInstance = createRouter({
      routeTree,
      basepath,
      // Use plain query params so id=id&type=GCPVertexAI (no JSON quotes around values)
      stringifySearch: (search) => {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(search)) {
          if (value !== undefined && value !== null && value !== '') {
            params.set(key, String(value));
          }
        }
        const s = params.toString();
        return s ? `?${s}` : '';
      },
      parseSearch: (searchStr) => {
        const params = new URLSearchParams(
          typeof searchStr === 'string' && searchStr.startsWith('?')
            ? searchStr.slice(1)
            : searchStr
        );
        const obj: Record<string, string> = {};
        params.forEach((value, key) => {
          obj[key] = value;
        });
        return obj;
      },
    });

    return routerInstance;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider locale='en' messages={messages} defaultLocale='en'>
        <AlertBannerProvider>
          <OverlayProvider>
            <RouterProvider router={router} />
          </OverlayProvider>
        </AlertBannerProvider>
      </IntlProvider>
    </QueryClientProvider>
  );
}
